import {
  arbitrum,
  avalanche,
  base,
  bsc,
  linea,
  mainnet,
  mantle,
  sepolia,
} from "viem/chains"
import * as abis from "../abi"
import * as lensAbis from "../abi/lens"
import * as addresses from "../addresses"
import {
  bartio,
  berachain,
  bob,
  hyperevm,
  ink,
  monad,
  plasma,
  sonic,
  swell,
  tac,
  unichain,
} from "../chains"
import { SUPPORTED_CHAIN_IDS, SupportedChainId } from "../config"

import { Address } from "viem"
import auxiliaryContracts from "./auxilaryContracts"

type Abis = typeof abis

type AddressesByChain =
  | typeof addresses.mainnetAddresses
  | typeof addresses.arbitrumAddresses
  | typeof addresses.bartioAddresses
  | typeof addresses.baseAddresses
  | typeof addresses.swellAddresses
  | typeof addresses.sonicAddresses
  | typeof addresses.inkAddresses
  | typeof addresses.unichainAddresses
  | typeof addresses.berachainAddresses
  | typeof addresses.avalancheAddresses
  | typeof addresses.bobAddresses
  | typeof addresses.bscAddresses
  | typeof addresses.hyperevmAddresses
  | typeof addresses.tacAddresses
  | typeof addresses.lineaAddresses
  | typeof addresses.mantleAddresses
  | typeof addresses.plasmaAddresses
  | typeof addresses.monadAddresses
// Generator Helper: @AddressesByChainType

type LensAbisByChain =
  | typeof lensAbis.mainnetLensAbis
  | typeof lensAbis.arbitrumLensAbis
  | typeof lensAbis.bartioLensAbis
  | typeof lensAbis.baseLensAbis
  | typeof lensAbis.swellLensAbis
  | typeof lensAbis.sonicLensAbis
  | typeof lensAbis.inkLensAbis
  | typeof lensAbis.unichainLensAbis
  | typeof lensAbis.berachainLensAbis
  | typeof lensAbis.avalancheLensAbis
  | typeof lensAbis.bobLensAbis
  | typeof lensAbis.bscLensAbis
  | typeof lensAbis.hyperevmLensAbis
  | typeof lensAbis.tacLensAbis
  | typeof lensAbis.lineaLensAbis
  | typeof lensAbis.mantleLensAbis
  | typeof lensAbis.plasmaLensAbis
  | typeof lensAbis.monadLensAbis
// Generator Helper: @AbisByChainType

type LensAddressesByChain = AddressesByChain["lensAddresses"]

const addressesByChain: Record<SupportedChainId, AddressesByChain> = {
  [mainnet.id]: addresses.mainnetAddresses,
  [arbitrum.id]: addresses.arbitrumAddresses,
  [bartio.id]: addresses.bartioAddresses,
  [sepolia.id]: addresses.mainnetAddresses,
  31337: addresses.mainnetAddresses, // Foundry
  41337: addresses.mainnetAddresses, // Tenderly
  [base.id]: addresses.baseAddresses,
  [swell.id]: addresses.swellAddresses,
  [sonic.id]: addresses.sonicAddresses,
  [ink.id]: addresses.inkAddresses,
  [unichain.id]: addresses.unichainAddresses,
  [berachain.id]: addresses.berachainAddresses,
  [avalanche.id]: addresses.avalancheAddresses,
  [bob.id]: addresses.bobAddresses,
  [bsc.id]: addresses.bscAddresses,
  [hyperevm.id]: addresses.hyperevmAddresses,
  [tac.id]: addresses.tacAddresses,
  [linea.id]: addresses.lineaAddresses,
  [mantle.id]: addresses.mantleAddresses,
  [plasma.id]: addresses.plasmaAddresses,
  [monad.id]: addresses.monadAddresses,
  // Generator Helper: @AddressesByChain
}

const lensAbisByChain = {
  [mainnet.id]: lensAbis.mainnetLensAbis,
  [arbitrum.id]: lensAbis.arbitrumLensAbis,
  [bartio.id]: lensAbis.bartioLensAbis,
  [sepolia.id]: lensAbis.mainnetLensAbis,
  31337: lensAbis.mainnetLensAbis,
  41337: lensAbis.mainnetLensAbis,
  [base.id]: lensAbis.baseLensAbis,
  [swell.id]: lensAbis.swellLensAbis,
  [sonic.id]: lensAbis.sonicLensAbis,
  [ink.id]: lensAbis.inkLensAbis,
  [unichain.id]: lensAbis.unichainLensAbis,
  [berachain.id]: lensAbis.berachainLensAbis,
  [avalanche.id]: lensAbis.avalancheLensAbis,
  [bob.id]: lensAbis.bobLensAbis,
  [bsc.id]: lensAbis.bscLensAbis,
  [hyperevm.id]: lensAbis.hyperevmLensAbis,
  [tac.id]: lensAbis.tacLensAbis,
  [linea.id]: lensAbis.lineaLensAbis,
  [mantle.id]: lensAbis.mantleLensAbis,
  [plasma.id]: lensAbis.plasmaLensAbis,
  [monad.id]: lensAbis.monadLensAbis,
  // Generator Helper: @AbisByChain
} as const

const lensAddressesByChain: Record<SupportedChainId, LensAddressesByChain> = {
  [mainnet.id]: addresses.mainnetAddresses.lensAddresses,
  [arbitrum.id]: addresses.arbitrumAddresses.lensAddresses,
  [bartio.id]: addresses.bartioAddresses.lensAddresses,
  [sepolia.id]: addresses.mainnetAddresses.lensAddresses,
  31337: addresses.mainnetAddresses.lensAddresses, // Foundry
  41337: addresses.mainnetAddresses.lensAddresses, // Tenderly
  [base.id]: addresses.baseAddresses.lensAddresses,
  [swell.id]: addresses.swellAddresses.lensAddresses,
  [sonic.id]: addresses.sonicAddresses.lensAddresses,
  [ink.id]: addresses.inkAddresses.lensAddresses,
  [unichain.id]: addresses.unichainAddresses.lensAddresses,
  [berachain.id]: addresses.berachainAddresses.lensAddresses,
  [avalanche.id]: addresses.avalancheAddresses.lensAddresses,
  [bob.id]: addresses.bobAddresses.lensAddresses,
  [bsc.id]: addresses.bscAddresses.lensAddresses,
  [hyperevm.id]: addresses.hyperevmAddresses.lensAddresses,
  [tac.id]: addresses.tacAddresses.lensAddresses,
  [linea.id]: addresses.lineaAddresses.lensAddresses,
  [mantle.id]: addresses.mantleAddresses.lensAddresses,
  [plasma.id]: addresses.plasmaAddresses.lensAddresses,
  [monad.id]: addresses.monadAddresses.lensAddresses,
  // Generator Helper: @LensAddressesByChain
}

export type IContractBook = typeof contractBook

// Helper function to create contract entries
const createLensContractEntryCommon = <TAbiKey extends keyof LensAbisByChain>(
  abiName: TAbiKey,
) => {
  type AddressValue = Address
  type AddressPropertyValue = AddressValue extends Record<Address, infer T>
    ? T
    : AddressValue
  type AbiValue = LensAbisByChain[TAbiKey]

  const abiEntries = SUPPORTED_CHAIN_IDS.map((chainId: SupportedChainId) => {
    return [chainId, lensAbisByChain[chainId][abiName]]
  })

  const singleAbi = lensAbisByChain[mainnet.id][abiName]

  return {
    abiEntries,
    AddressPropertyValue: {} as AddressPropertyValue,
    AbiValue: {} as AbiValue,
    singleAbi,
  }
}

const createLensSingleContractEntry = <
  TAddressValue extends keyof LensAddressesByChain,
  TAbiKey extends keyof LensAbisByChain,
>(
  addressesProperty: TAddressValue,
  abiName: TAbiKey,
) => {
  const { abiEntries, AddressPropertyValue, AbiValue, singleAbi } =
    createLensContractEntryCommon(abiName)

  const addressEntries = SUPPORTED_CHAIN_IDS.map(
    (chainId: SupportedChainId) => [
      chainId,
      lensAddressesByChain[chainId][addressesProperty],
    ],
  )

  const entry: {
    address: Record<SupportedChainId, typeof AddressPropertyValue>
    abi: typeof lensAbisByChain[typeof mainnet.id][TAbiKey]
    abiByChain: Record<SupportedChainId, typeof AbiValue>
  } = {
    abiByChain: Object.fromEntries(abiEntries),
    abi: singleAbi,
    address: Object.fromEntries(addressEntries),
  }

  return entry
}

const createContractAbiEntry = <TAbiKey extends keyof Abis>(abi: TAbiKey) => {
  const singleAbi = abis[abi]

  const entry: {
    abi: typeof singleAbi
  } = {
    abi: singleAbi,
  }

  return entry
}

function createSingleContractEntry<
  TAddressKey extends keyof AddressesByChain,
  TAddressValue extends keyof AddressesByChain[TAddressKey],
  TAbiKey extends keyof Abis,
>(
  addressesKey: TAddressKey,
  addressesProperty: TAddressValue,
  abiName: TAbiKey,
) {
  const addressEntries = SUPPORTED_CHAIN_IDS.map(
    (chainId: SupportedChainId) => [
      chainId,
      addressesByChain[chainId][addressesKey][addressesProperty],
    ],
  )

  type AbiValue = Abis[TAbiKey]
  type AddressValue = Address
  type AddressPropertyValue = AddressValue extends Record<Address, infer T>
    ? T
    : AddressValue

  const singleAbi = abis[abiName]

  const entry: {
    address: Record<SupportedChainId, AddressPropertyValue>
    abi: AbiValue
  } = {
    abi: singleAbi,
    address: Object.fromEntries(addressEntries),
  }

  return entry
}

const createArrayContractEntry = <
  TAddressKey extends keyof AddressesByChain,
  TAddressValue extends keyof AddressesByChain[TAddressKey],
  TAbiKey extends keyof Abis,
>(
  addressesKey: TAddressKey,
  addressesProperty: TAddressValue,
  abiName: TAbiKey,
) => {
  type AbiValue = Abis[TAbiKey]
  type AddressValue = Address
  type AddressPropertyValue = AddressValue extends Record<Address, infer T>
    ? T
    : AddressValue

  const singleAbi = abis[abiName]

  const addressEntries = SUPPORTED_CHAIN_IDS.map(
    (chainId: SupportedChainId) => [
      chainId,
      [addressesByChain[chainId][addressesKey][addressesProperty]],
    ],
  )

  const entry: {
    address: Record<SupportedChainId, AddressPropertyValue[]>
    abi: AbiValue
  } = {
    abi: singleAbi,
    address: Object.fromEntries(addressEntries),
  }

  return entry
}

/**
 * CORE CONTRACTS
 * =============================================
 *
 * The addresses and ABIs below are extracted from euler-interfaces
 * repository and are generated per chain using the script:
 * ./scripts/abisAddressesSync.sh <network_id>
 *
 * createContractEntry helper function takes:
 * - addressType: The type of address (e.g. 'peripheryAddresses', 'coreAddresses')
 * - addressKey: The key to look up the address in the addresses object
 * - abiKey: The key to look up the ABI in the abis object
 *
 * This allows us to dynamically generate contract entries with
 * chain-specific addresses and ABIs.
 */

const mainContracts = {
  swapper: createSingleContractEntry(
    "peripheryAddresses",
    "swapper",
    "swapperAbi",
  ),
  swapVerifier: createSingleContractEntry(
    "peripheryAddresses",
    "swapVerifier",
    "swapVerifierAbi",
  ),
  rewardStreams: createSingleContractEntry(
    "coreAddresses",
    "balanceTracker",
    "trackingRewardStreamsAbi",
  ),
  termsOfUseSigner: createSingleContractEntry(
    "peripheryAddresses",
    "termsOfUseSigner",
    "termsOfUseSignerAbi",
  ),
  evc: createSingleContractEntry(
    "coreAddresses",
    "evc",
    "ethereumVaultConnectorAbi",
  ),
  accountLens: createLensSingleContractEntry("accountLens", "accountLensAbi"),
  vaultLens: createLensSingleContractEntry("vaultLens", "vaultLensAbi"),
  eulerEarnVaultLens: createLensSingleContractEntry(
    "eulerEarnVaultLens",
    "eulerEarnVaultLensAbi",
  ),
  feeFlow: createSingleContractEntry(
    "peripheryAddresses",
    "feeFlowController",
    "feeFlowControllerAbi",
  ),
  feeFlowUtils: createSingleContractEntry(
    "peripheryAddresses",
    "feeFlowControllerUtil",
    "feeFlowControllerAbi",
  ),
} as const

const extraLensContracts = {
  utilsLens: createLensSingleContractEntry("utilsLens", "utilsLensAbi"),
  oracleLens: createLensSingleContractEntry("oracleLens", "oracleLensAbi"),
} as const

const perspectiveContracts = {
  basePerspective: createContractAbiEntry("basePerspectiveAbi"),
  eulerEarnGovernedPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "eulerEarnGovernedPerspective",
    "basePerspectiveAbi",
  ),
  eulerEarnFactoryPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "eulerEarnFactoryPerspective",
    "basePerspectiveAbi",
  ),
  edgePerspective: createArrayContractEntry(
    "peripheryAddresses",
    "edgeFactoryPerspective",
    "basePerspectiveAbi",
  ),
  escrowPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "escrowedCollateralPerspective",
    "basePerspectiveAbi",
  ),
  governedPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "governedPerspective",
    "basePerspectiveAbi",
  ),
  ungoverned0xPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "eulerUngoverned0xPerspective",
    "basePerspectiveAbi",
  ),
  ungovernedNzxPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "eulerUngovernedNzxPerspective",
    "basePerspectiveAbi",
  ),
  factoryPerspective: createArrayContractEntry(
    "peripheryAddresses",
    "evkFactoryPerspective",
    "basePerspectiveAbi",
  ),
  eVault: createContractAbiEntry("eVaultAbi"),
} as const

const tokenContracts = {
  eul: createSingleContractEntry("tokenAddresses", "EUL", "erc20Abi"),
  rEul: createSingleContractEntry("tokenAddresses", "rEUL", "rewardTokenAbi"),
}

const eulerSwapContracts = {
  eulerSwapFactory: createSingleContractEntry(
    "eulerSwapAddresses",
    "eulerSwapV1Factory",
    "eulerSwapFactoryAbi",
  ),
  eulerSwap: createSingleContractEntry(
    "eulerSwapAddresses",
    "eulerSwapV1Implementation",
    "eulerSwapAbi",
  ),
}

type ContractBook = typeof mainContracts &
  typeof perspectiveContracts &
  typeof tokenContracts &
  typeof auxiliaryContracts &
  typeof eulerSwapContracts &
  typeof extraLensContracts

export const contractBook: ContractBook = {
  ...mainContracts,
  ...perspectiveContracts,
  ...tokenContracts,
  ...auxiliaryContracts,
  ...eulerSwapContracts,
  ...extraLensContracts,
} as const
